/**
 * Toaster — renders toast notifications in a fixed overlay.
 *
 * Place once at the app root. Toasts slide in from the bottom-right.
 */

import { X } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';
import { type ToastVariant,useToastStore } from '@/store/useToastStore';

const variantStyles: Record<ToastVariant, string> = {
  default: 'bg-background border-border text-foreground',
  success: 'bg-background border-emerald-500/40 text-foreground',
  warning: 'bg-background border-amber-500/40 text-foreground',
  error: 'bg-background border-destructive/40 text-foreground',
};

export const Toaster: React.FC = () => {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          aria-live="polite"
          className={cn(
            'pointer-events-auto flex items-center gap-3 rounded-lg border px-4 py-3 shadow-lg',
            'animate-in slide-in-from-bottom-2 fade-in-0 duration-200',
            'text-sm max-w-sm',
            variantStyles[t.variant],
          )}
        >
          <span className="flex-1">{t.message}</span>
          <button
            onClick={() => removeToast(t.id)}
            className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};
